/* ------------------------------------------------------------------
   frontend/src/App.jsx
------------------------------------------------------------------ */
import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useState, useEffect } from 'react';

import Sidebar       from './components/Sidebar';
import Topbar        from './components/Topbar'; // topo para mobile
import api from './api';
import { formatDateTimeBR } from './utils/datetime';
const MachinesPage = lazy(() => import('./pages/Machines'));
const MachineLocationsPage = lazy(() => import('./pages/MachineLocations'));
const OfflineMachinesLogPage = lazy(() => import('./pages/OfflineMachinesLog'));
const InventoryPage = lazy(() => import('./pages/Inventory'));
const TemplatesPage = lazy(() => import('./pages/Templates'));
const SendCmdPage = lazy(() => import('./pages/SendCmd'));
const SendBulkPage = lazy(() => import('./pages/SendBulk'));
const ScriptsSendPage = lazy(() => import('./pages/ScriptsSend'));
const ApplicationsPage = lazy(() => import('./pages/Applications'));
const BulkLogsPage = lazy(() => import('./pages/BulkLogs'));
const BrandingPage = lazy(() => import('./pages/Branding'));
const UsersPage = lazy(() => import('./pages/Users'));
const CompaniesPage = lazy(() => import('./pages/Companies'));
const DepartmentsPage = lazy(() => import('./pages/Departments'));
const PrintersPage = lazy(() => import('./pages/Printers'));
const VpnProfilesPage = lazy(() => import('./pages/VpnProfiles'));
const VpnConnectionsPage = lazy(() => import('./pages/VpnConnections'));
const VpnTopologyPage = lazy(() => import('./pages/VpnTopology'));
const VpnDirectLinksPage = lazy(() => import('./pages/VpnDirectLinks'));
const RustDeskConfigPage = lazy(() => import('./pages/RustDeskConfig'));
const RustDeskServerPeersPage = lazy(() => import('./pages/RustDeskServerPeers'));
const PublicLinksPage = lazy(() => import('./pages/PublicLinks'));
const WhatsAppConnectionsPage = lazy(() => import('./pages/WhatsAppConnections'));
const AttendanceDashboardPage = lazy(() => import('./pages/AttendanceDashboard'));
const TicketsPage = lazy(() => import('./pages/Tickets'));
const TicketAgendasPage = lazy(() => import('./pages/TicketAgendas'));
const ContactsPage = lazy(() => import('./pages/Contacts'));
const CollaboratorsPage = lazy(() => import('./pages/Collaborators'));
const ITProductsPage = lazy(() => import('./pages/ITProducts'));
const ITServiceOrdersPage = lazy(() => import('./pages/ITServiceOrders'));
const AgentsPage = lazy(() => import('./pages/Agents'));
const Login = lazy(() => import('./pages/Login'));
const PublicPage = lazy(() => import('./pages/Public'));

const AGENDA_NOTIFIED_KEY = 'ticket_agendas_notified';

function PageFallback() {
  return <div className="p-4 text-sm text-zinc-500">Carregando...</div>;
}

function readCurrentRole() {
  try {
    const payload = localStorage.getItem('token')?.split('.')?.[1];
    if (!payload) return 'user';
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (base64.length % 4)) % 4);
    return String(JSON.parse(atob(base64 + pad))?.role || 'user').toLowerCase();
  } catch {
    return 'user';
  }
}

function MasterRoute({ children }) {
  return readCurrentRole() === 'master'
    ? children
    : <Navigate to="/machines" replace />;
}


function readAgendaNotifiedMap() {
  try {
    const parsed = JSON.parse(localStorage.getItem(AGENDA_NOTIFIED_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeAgendaNotifiedMap(map) {
  try {
    localStorage.setItem(AGENDA_NOTIFIED_KEY, JSON.stringify(map || {}));
  } catch {}
}

export default function App() {
  const [logged, setLogged] = useState(Boolean(localStorage.getItem('token')));
  const [drawer, setDrawer] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);

  useEffect(() => {
    const sync = () => setLogged(Boolean(localStorage.getItem('token')));
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const logout = () => {
    localStorage.clear();
    setLogged(false);
  };

  useEffect(() => {
    if (!logged) return undefined;
    if (typeof window === 'undefined') return undefined;

    let cancelled = false;
    let timer = null;

    const requestNotificationPermission = () => {
      if (!('Notification' in window)) return;
      if (Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    };

    const notifyDueAgendas = async () => {
      const now = Date.now();
      try {
        const rows = await api.listTicketAgendas({
          status: 'pending',
          date_to: new Date(now).toISOString(),
          limit: 20,
        });
        if (cancelled) return;

        const due = (Array.isArray(rows) ? rows : []).filter((agenda) => {
          const scheduled = Date.parse(agenda?.scheduled_at || '');
          return Number.isFinite(scheduled) && scheduled <= now;
        });
        if (!due.length) return;

        requestNotificationPermission();
        const seen = readAgendaNotifiedMap();
        const cutoff = now - 30 * 24 * 60 * 60 * 1000;
        const nextSeen = Object.fromEntries(
          Object.entries(seen).filter(([, ts]) => Number(ts || 0) >= cutoff)
        );
        let changed = false;

        for (const agenda of due) {
          const key = `${agenda.id}:${agenda.scheduled_at || ''}`;
          if (nextSeen[key]) continue;
          nextSeen[key] = now;
          changed = true;

          if ('Notification' in window && Notification.permission === 'granted') {
            const title = `Agenda do ticket #${agenda.ticket_id}`;
            const body = `${formatDateTimeBR(agenda.scheduled_at)} - ${String(agenda.note || 'Agenda pendente').slice(0, 120)}`;
            try { new Notification(title, { body }); } catch {}
          }
        }

        if (changed) writeAgendaNotifiedMap(nextSeen);
      } catch {
        // Silencioso: agenda vencida aparece na tela propria e a proxima rodada tenta de novo.
      }
    };

    notifyDueAgendas();
    timer = window.setInterval(notifyDueAgendas, 2 * 60 * 1000);

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [logged]);

  if (!logged) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* rota pública, sem login */}
          <Route path="/public/:token" element={<PublicPage />} />

          {/* qualquer outra rota cai no login */}
          <Route path="*" element={<Login onLogin={() => setLogged(true)} />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen md:flex bg-gray-50 overflow-x-hidden">
      <Sidebar
        onLogout={logout}
        isOpen={drawer}
        onClose={() => setDrawer(false)}
        isDesktopHidden={sidebarHidden}
        onHideDesktop={() => setSidebarHidden(true)}
      />

      {/* md:ml-64 compensa a sidebar fixa; min-w-0 evita compressão */}
      <div className={`flex-1 min-w-0 ${sidebarHidden ? '' : 'md:ml-64'}`}>
        <Topbar onMenu={() => setDrawer(true)} />

        {/* padding enxuto + scroll apenas no conteúdo */}
        <main className="px-3 md:pl-4 md:pr-6 py-4 overflow-auto">
          {sidebarHidden && (
            <div className="hidden md:flex mb-3">
              <button
                type="button"
                onClick={() => setSidebarHidden(false)}
                className="px-3 py-2 rounded-lg bg-zinc-200 text-sm"
              >
                Mostrar menu
              </button>
            </div>
          )}
          <Suspense fallback={<PageFallback />}>
            <Routes>
            {/* redirecionamentos */}
            <Route path="/"          element={<Navigate to="/machines" replace />} />
            <Route path="/dashboard" element={<AttendanceDashboardPage />} />
            <Route path="/attendance-dashboard" element={<AttendanceDashboardPage />} />

            {/* páginas */}
            <Route path="/machines"   element={<MachinesPage  />} />
            <Route path="/machine-locations" element={<MachineLocationsPage />} />
            <Route path="/offline-machines" element={<OfflineMachinesLogPage />} />
            <Route path="/inventory"  element={<InventoryPage />} />
            <Route path="/online"     element={<Navigate to="/machines" replace />} />
            <Route path="/templates" element={<MasterRoute><TemplatesPage /></MasterRoute>} />
            <Route path="/send" element={<MasterRoute><SendCmdPage /></MasterRoute>} />
            <Route path="/send-bulk" element={<MasterRoute><SendBulkPage /></MasterRoute>} />
            <Route path="/scripts-send" element={<MasterRoute><ScriptsSendPage /></MasterRoute>} />
            <Route path="/applications" element={<MasterRoute><ApplicationsPage /></MasterRoute>} />
            <Route path="/public-links" element={<MasterRoute><PublicLinksPage /></MasterRoute>} />
            <Route path="/whatsapp-connections" element={<WhatsAppConnectionsPage />} />
            <Route path="/tickets" element={<TicketsPage />} />
            <Route path="/ticket-agendas" element={<TicketAgendasPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/collaborators" element={<CollaboratorsPage />} />
            <Route path="/it-products" element={<ITProductsPage />} />
            <Route path="/it-service-orders" element={<ITServiceOrdersPage />} />
            <Route path="/agents" element={<MasterRoute><AgentsPage /></MasterRoute>} />
            <Route path="/bulk-logs" element={<MasterRoute><BulkLogsPage /></MasterRoute>} />
            <Route path="/branding"   element={<BrandingPage  />} />
            <Route path="/users"      element={<UsersPage     />} />
            <Route path="/companies"  element={<CompaniesPage />} />
            <Route path="/departments"  element={<DepartmentsPage />} />
            <Route path="/printers"  element={<PrintersPage />} />
            <Route path="/rustdesk" element={<RustDeskConfigPage />} />
            <Route path="/rustdesk-peers" element={<RustDeskServerPeersPage />} />
            <Route path="/vpn" element={<VpnProfilesPage />} />
            <Route path="/vpn-connections" element={<VpnConnectionsPage />} />
            <Route path="/vpn-map" element={<VpnTopologyPage />} />
            <Route path="/vpn-direct" element={<VpnDirectLinksPage />} />
            <Route path="/public/:token" element={<PublicPage />} />

            {/* fallback 404 */}
            <Route
              path="*"
              element={<p className="text-red-600">404 - página não encontrada</p>}
            />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}
